import React, { useState } from 'react';
import { Upload, X } from 'lucide-react';

interface FileUploadProps {
  id: string;
  accept?: string;
  maxSize?: number; // em bytes
  allowedTypes?: string[];
  onFileChange: (file: File | null) => void;
  showPreview?: boolean;
  previewHeight?: string;
  placeholder?: {
    title: string;
    subtitle: string;
  };
  className?: string;
  existingImageUrl?: string | null; // URL de imagem existente
}

const FileUpload: React.FC<FileUploadProps> = ({
  id,
  accept = "image/*",
  maxSize = 5 * 1024 * 1024, // 5MB por padrão
  allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'],
  onFileChange,
  showPreview = true,
  previewHeight = "h-48",
  placeholder = {
    title: "Clique para enviar",
    subtitle: "PNG, JPG ou JPEG (MAX. 5MB)"
  },
  className = "",
  existingImageUrl = null
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (selectedFile: File | null) => {
    setError(null);
    
    if (selectedFile) {
      // Verificar tamanho do arquivo
      if (selectedFile.size > maxSize) {
        const maxSizeMB = maxSize / (1024 * 1024);
        setError(`Erro: O arquivo deve ter no máximo ${maxSizeMB}MB.`);
        return;
      }
      
      // Verificar tipo do arquivo
      if (allowedTypes.length > 0 && !allowedTypes.includes(selectedFile.type)) {
        const types = allowedTypes.map(type => type.split('/')[1].toUpperCase()).join(', ');
        setError(`Erro: Apenas arquivos ${types} são permitidos.`);
        return;
      }
    }
    
    setFile(selectedFile);
    
    if (selectedFile && showPreview && selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(selectedFile);
    } else {
      setPreview(null);
    }
    
    onFileChange(selectedFile);
  };

  const handleRemove = () => {
    handleFileChange(null);
    // Aqui poderíamos adicionar lógica adicional se necessário para limpar a imagem existente
  };

  const isImage = file && file.type.startsWith('image/');

  return (
    <div className={className}>
      <input
        type="file"
        id={id}
        accept={accept}
        className="sr-only"
        onChange={(e) => {
          const selectedFile = e.target.files?.[0] || null;
          handleFileChange(selectedFile);
        }}
      />

      {showPreview && ((file && isImage && preview) || (!file && existingImageUrl)) ? (
        <div className={`relative w-full ${previewHeight} border-2 border-gray-300 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center`}>
          <img
            src={preview || existingImageUrl || ''}
            alt="Preview do arquivo"
            className="max-w-full max-h-full object-contain"
          />
          <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
            <div className="flex gap-2">
              <label
                htmlFor={id}
                className="px-3 py-1 bg-white text-gray-800 rounded cursor-pointer hover:bg-gray-100 text-sm font-medium"
              >
                {file || existingImageUrl ? "Alterar" : "Enviar"}
              </label>
              {(file || existingImageUrl) && (
                <button
                  type="button"
                  onClick={handleRemove}
                  className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm font-medium"
                >
                  Remover
                </button>
              )}
            </div>
          </div>
        </div>
      ) : file ? (
        // Preview para arquivos não-imagem
        <div className="flex items-center justify-between p-3 border-2 border-gray-300 rounded-lg bg-gray-50">
          <div className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-gray-500" />
            <span className="text-sm text-gray-700 truncate">{file.name}</span>
          </div>
          <div className="flex gap-2">
            <label
              htmlFor={id}
              className="px-2 py-1 bg-blue-600 text-white rounded cursor-pointer hover:bg-blue-700 text-xs font-medium"
            >
              Alterar
            </label>
            <button
              type="button"
              onClick={handleRemove}
              className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs font-medium"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      ) : (
        // Estado inicial - sem arquivo
        <label
          htmlFor={id}
          className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors duration-200"
        >
          <div className="flex flex-col items-center justify-center pt-5 pb-6">
            <Upload className="w-8 h-8 mb-3 text-gray-400" />
            <p className="mb-2 text-sm text-gray-500">
              <span className="font-semibold">{placeholder.title}</span>
            </p>
            <p className="text-xs text-gray-500">{placeholder.subtitle}</p>
          </div>
        </label>
      )}

      {error && (
        <p className="mt-2 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default FileUpload;